import { useState } from 'react';
import { useNavigate, useSearch } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { LogIn } from 'lucide-react';
import { localLogin } from '@vinyl/shared';
import { api, ApiError } from '../lib/api.ts';
import { errorText } from '../lib/error-text.ts';
import { meQueryKey, useAuthConfig } from '../auth/session.ts';
import { Button } from '../components/ui/button.tsx';
import { Input, Label } from '../components/ui/input.tsx';
import { HelpButton, useHelpPage } from '../help/help.tsx';

function LocalForm() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <form
      className="flex flex-col gap-4"
      noValidate
      onSubmit={async (event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        setBusy(true);
        setError(null);
        try {
          const me = await api(localLogin, {
            body: { username: String(data.get('username') ?? ''), password: String(data.get('password') ?? '') },
          });
          queryClient.setQueryData(meQueryKey, me);
          await navigate({ to: '/collection' });
        } catch (err) {
          setError(err instanceof ApiError ? errorText(t, err) : t('errors.internal'));
        } finally {
          setBusy(false);
        }
      }}
    >
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="username">{t('signIn.username')}</Label>
        <Input id="username" name="username" autoComplete="username" autoCapitalize="none" spellCheck={false} required data-help-id="signIn.username" />
      </div>
      <div className="flex flex-col gap-1.5">
        <Label htmlFor="password">{t('signIn.password')}</Label>
        <Input id="password" name="password" type="password" autoComplete="current-password" required data-help-id="signIn.password" />
      </div>
      {error ? (
        <p role="alert" className="rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm">
          {error}
        </p>
      ) : null}
      <Button type="submit" disabled={busy} data-help-id="signIn.submit">
        <LogIn aria-hidden />
        {t('signIn.submit')}
      </Button>
      <p className="text-sm text-muted-foreground">{t('signIn.forgot')}</p>
      <p className="text-sm text-muted-foreground">{t('signIn.noAccount')}</p>
    </form>
  );
}

function OidcButton({ host }: { host: string | null }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col gap-4">
      <Button asChild data-help-id="signIn.provider">
        <a href="/auth/login">
          <LogIn aria-hidden />
          {t('signIn.withProvider')}
        </a>
      </Button>
      {host ? <p className="text-sm text-muted-foreground">{t('signIn.providerHost', { host })}</p> : null}
    </div>
  );
}

export function SignInPage() {
  const { t } = useTranslation();
  useHelpPage('signIn');
  const config = useAuthConfig();
  const search = useSearch({ strict: false }) as { error?: string };
  return (
    <div className="flex min-h-dvh items-center justify-center px-4 py-10">
      <main className="w-full max-w-sm">
        <div className="mb-8 flex items-center justify-between">
          <h1 className="font-display text-3xl text-primary">{t('app.name')}</h1>
          <HelpButton />
        </div>
        <section className="rounded-xl border border-border bg-card p-6 shadow-sm">
          <h2 className="mb-1 text-xl">{t('signIn.title')}</h2>
          <p className="mb-6 text-sm text-muted-foreground">{t('signIn.intro')}</p>
          {search.error === 'callback_failed' ? (
            <p role="alert" className="mb-4 rounded-lg border border-destructive/50 bg-destructive/10 px-3 py-2 text-sm">
              {t('errors.auth.callback_failed')}
            </p>
          ) : null}
          {config.data?.mode === 'oidc' ? <OidcButton host={config.data.providerHost} /> : config.data ? <LocalForm /> : null}
        </section>
      </main>
    </div>
  );
}
